/**
 * Shared scan-trigger/status client for Sentinel's security scanning pipeline
 * (backed by `/extract`, which queues an AWS Batch job via SQS). Centralized here so
 * project cards, the project detail page, and the onboarding wizard all use the same
 * contract instead of re-implementing the same POST/GET calls.
 */
import sentinelNetwork from './sentinelNetwork';

export type ScanCategory = 'deep-ai' | 'epa' | 'security-profile';

export interface ExtractionStatus {
  extractionStatus: 'running' | 'completed' | 'failed' | null;
  extractionStep?: string;
  extractionError?: string;
  [key: string]: any;
}

export type ExtractionStatuses = Record<ScanCategory, ExtractionStatus | null>;

/** Returns the extraction/scan status for a single category, or null if none is on record. */
export async function getExtractionStatus(appId: string, category: ScanCategory = 'deep-ai'): Promise<ExtractionStatus | null> {
  const { data } = await sentinelNetwork.get('/extract', { params: { appId, category } });
  return data?.extractionStatus ? data : null;
}

/** Returns all per-category extraction statuses for a project. */
export async function getExtractionStatuses(appId: string): Promise<ExtractionStatuses> {
  const { data } = await sentinelNetwork.get('/extract', { params: { appId } });
  const statuses: ExtractionStatuses = { 'deep-ai': null, epa: null, 'security-profile': null };
  if (data && typeof data === 'object') {
    for (const key of Object.keys(data)) {
      if (data[key]?.extractionStatus) {
        statuses[key as ScanCategory] = data[key];
      }
    }
  }
  return statuses;
}

/** Queues a scan for a single project. */
export async function triggerScan(
  appId: string,
  scanType: 'delta' | 'complete' = 'complete',
  scanCategory: ScanCategory = 'deep-ai'
): Promise<void> {
  const payload: Record<string, any> = { appId, scanType, scanCategory };
  if (scanCategory !== 'deep-ai') {
    payload.onDemandCategory = scanCategory;
  }
  await sentinelNetwork.post('/extract', payload);
}

/** Queues a scan for multiple projects at once (e.g. "Scan All") — backed by the admin-only bulk extraction endpoint. */
export async function triggerBulkScan(appIds: string[], scanType: 'delta' | 'complete' = 'complete'): Promise<void> {
  if (appIds.length === 0) return;
  await sentinelNetwork.post('/extract-all', { appIds, scanType });
}

/** Bulk delete projects (SENTINEL_POWER_USER only). */
export async function deleteProjects(appIds: string[]) {
  return sentinelNetwork.post('/config/admin/bulk-delete', { appIds });
}

/** Bulk run Sonar / BlackDuck / Trivy scans for selected projects (SENTINEL_POWER_USER only). */
export async function runBulkScan(tool: string, appIds: string[]) {
  return sentinelNetwork.post('/config/admin/bulk-scan', { tool, appIds });
}

/** Triggers an on-demand scan for a specific category (e.g. 'epa' or 'security-profile').
 *  The backend resolves all enabled prompts for the given category and runs only those,
 *  merging results back into the existing sentinel document without overwriting other fields.
 *  @param agentMode Optional AI engine override (e.g. 'devin', 'deepagent'). Omit to use project default.
 */
export async function triggerOnDemandScan(appId: string, category: 'epa' | 'security-profile', agentMode?: string): Promise<void> {
  await sentinelNetwork.post('/extract', { appId, scanType: 'complete', onDemandCategory: category, ...(agentMode ? { agentMode } : {}) });
}
